import { Button, Collapse, Tooltip } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import UnarchiveOutlinedIcon from '@mui/icons-material/UnarchiveOutlined';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import AddIcon from '@mui/icons-material/Add';
import RowActions, { type RowAction } from '../../../../components/RowActions';
import type { SubcontractorUser } from '../../../planning/types';
import type { SettingsSubcontractorRow } from '../../types';
import { SUBCONTRACTOR_ROW_LABELS as LABELS } from '../../../../utils/labels';

interface UserRowProps {
  user: SubcontractorUser;
  readOnly?: boolean;
  onSetDefault: () => void;
  onEdit: () => void;
  onDelete: () => void;
}

function UserRow({ user, readOnly, onSetDefault, onEdit, onDelete }: UserRowProps) {
  const actions: RowAction[] = readOnly
    ? []
    : [
        ...(!user.is_default
          ? [{ label: LABELS.setDefault, icon: <StarBorderIcon fontSize="small" />, onClick: onSetDefault, className: 'text-text-disabled', testId: 'set-default-button' }]
          : []),
        { label: LABELS.editUser, icon: <EditIcon fontSize="small" />, onClick: onEdit, testId: 'edit-button' },
        { label: LABELS.deleteUser, icon: <DeleteOutlineIcon fontSize="small" />, onClick: onDelete, color: 'error' as const, testId: 'delete-button' },
      ];

  return (
    <div>
      <hr className="border-t border-border mb-0 mt-0" />
      <div className="flex items-center gap-2 pl-12 pr-3 py-3">
        <span className="flex-1 text-sm min-w-0 truncate">
          {user.first_name} {user.last_name}
        </span>
        {user.is_default && (
          <Tooltip title={LABELS.isDefault}>
            <span className="text-amber-400 flex items-center shrink-0">
              <StarIcon fontSize="small" />
            </span>
          </Tooltip>
        )}
        {!readOnly && (
          <div className="flex items-center gap-1 shrink-0">
            <RowActions actions={actions} />
          </div>
        )}
      </div>
    </div>
  );
}

interface SubcontractorRowProps {
  subcontractor: SettingsSubcontractorRow;
  showDivider: boolean;
  expanded: boolean;
  onToggle: () => void;
  onEdit?: () => void;
  onArchive?: () => void;
  onUnarchive?: () => void;
  onDelete?: () => void;
  onAddUser?: () => void;
  onEditUser?: (user: SubcontractorUser) => void;
  onDeleteUser?: (id: string) => void;
  onSetDefaultUser?: (userId: string) => void;
}

export default function SubcontractorRow({
  subcontractor,
  showDivider,
  expanded,
  onToggle,
  onEdit,
  onArchive,
  onUnarchive,
  onDelete,
  onAddUser,
  onEditUser,
  onDeleteUser,
  onSetDefaultUser,
}: SubcontractorRowProps) {
  const users = subcontractor.subcontractor_users;

  const actions: RowAction[] = subcontractor.archived
    ? onUnarchive
      ? [{ label: LABELS.unarchive, icon: <UnarchiveOutlinedIcon fontSize="small" />, onClick: onUnarchive, color: 'success', testId: 'unarchive-button' }]
      : []
    : [
        ...(onEdit ? [{ label: LABELS.edit, icon: <EditIcon fontSize="small" />, onClick: onEdit, color: 'primary' as const, testId: 'edit-button' }] : []),
        ...(subcontractor.has_bookings
          ? onArchive
            ? [{ label: LABELS.archive, icon: <ArchiveOutlinedIcon fontSize="small" />, onClick: onArchive, color: 'warning' as const, testId: 'archive-button' }]
            : []
          : onDelete
            ? [{ label: LABELS.delete, icon: <DeleteOutlineIcon fontSize="small" />, onClick: onDelete, color: 'error' as const, testId: 'delete-button' }]
            : []),
      ];

  return (
    <div>
      {showDivider && <hr className="border-t border-border mb-0 mt-0" />}

      <div className="flex items-center gap-2 px-3 py-2">
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={expanded}
          className="flex items-center gap-2 flex-1 min-w-0 text-left bg-transparent border-0 cursor-pointer"
        >
          <ExpandMoreIcon
            className={`text-text-disabled shrink-0 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`}
          />
          <span className={`text-base font-bold truncate ${subcontractor.archived ? 'text-text-secondary' : ''}`}>
            {subcontractor.name}
          </span>
        </button>

        <div className="flex items-center gap-1 shrink-0">
          <RowActions actions={actions} />
        </div>
      </div>

      <Collapse in={expanded}>
        <div>
          {users.length === 0 && (
            <>
              <hr className="border-t border-border mb-0 mt-0" />
              <p className="text-xs text-text-disabled px-12 py-2">{LABELS.noUsers}</p>
            </>
          )}
          {users.map((u) => (
            <UserRow
              key={u.id}
              user={u}
              readOnly={subcontractor.archived || !onEditUser}
              onSetDefault={() => onSetDefaultUser?.(u.id)}
              onEdit={() => onEditUser?.(u)}
              onDelete={() => onDeleteUser?.(u.id)}
            />
          ))}
          {!subcontractor.archived && onAddUser && (
            <div className="px-4 py-2 border-t border-border">
              <Button startIcon={<AddIcon />} onClick={onAddUser} color="primary" data-testid="add-user-button">
                {LABELS.addUser}
              </Button>
            </div>
          )}
        </div>
      </Collapse>
    </div>
  );
}
